import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { logger } from '../utils/logger';
import { userService } from '../services/userService';

const CSS_VAR_TEXT_PRIMARY = 'var(--text-primary)';

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await userService.getProfile();

      // Pre-fill with username if displayName is not set
      setDisplayName(data.displayName || data.username);
      setBio(data.bio || '');
    } catch (err: any) {
      logger.error('Error loading profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleComplete = async () => {
    try {
      setSaving(true);
      setError(null);

      // Step 1: Upload profile picture if selected
      if (selectedFile) {
        setUploading(true);

        // Get presigned URL
        const { uploadUrl, key } = await userService.generateProfilePictureUploadUrl(
          selectedFile.name,
          selectedFile.type,
          selectedFile.size
        );

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: {
            'Content-Type': selectedFile.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file to S3');
        }

        // Validate the uploaded image
        const validation = await userService.validateProfilePicture(key);
        if (!validation.isValid) {
          throw new Error('Image validation failed. Please upload an appropriate image.');
        }

        setUploading(false);
      }

      // Step 2: Update profile with display name and bio
      await userService.updateProfile({
        displayName: displayName.trim() || undefined,
        bio: bio.trim() || undefined,
      });

      // Navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      logger.error('Error completing profile:', err);
      setError(err.response?.data?.message || err.message || 'Failed to complete profile');
      setSaving(false);
      setUploading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { replace: true });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ marginBottom: '0.5rem', color: 'var(--heading-color)', fontFamily: 'Syne, sans-serif' }}>Complete Your Profile</h1>
        <p style={{ color: CSS_VAR_TEXT_PRIMARY, marginBottom: '2rem' }}>
          Let's set up your profile! You can skip this step and complete it later from your profile page.
        </p>

        {error && (
          <div className="error-message" style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#fee',
            color: '#c00',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        {/* Profile Picture Section */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '1rem', color: CSS_VAR_TEXT_PRIMARY }}>
            Profile Picture (Optional)
          </label>

          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Profile preview"
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
            ) : (
              <div style={{
                width: '150px',
                height: '150px',
                margin: '0 auto',
                borderRadius: '8px',
                backgroundColor: 'var(--background-elevated)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: CSS_VAR_TEXT_PRIMARY,
                border: '2px dashed var(--border-color)'
              }}>
                No photo selected
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <label style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              fontSize: '0.875rem'
            }}>
              {selectedFile ? 'Change Photo' : 'Select Photo'}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={saving}
                style={{ display: 'none' }}
              />
            </label>

            {selectedFile && (
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                  fontSize: '0.875rem'
                }}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Display Name Field */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: CSS_VAR_TEXT_PRIMARY }}>
            Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              fontSize: '1rem',
              backgroundColor: 'var(--background-card)',
              color: CSS_VAR_TEXT_PRIMARY
            }}
            placeholder="How should we call you?"
            disabled={saving}
            maxLength={50}
          />
          <small style={{ color: CSS_VAR_TEXT_PRIMARY, fontSize: '0.875rem' }}>
            This is how your name will appear to other users. Defaults to your username if left empty.
          </small>
        </div>

        {/* Bio Field */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: CSS_VAR_TEXT_PRIMARY }}>
            Bio (Optional)
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              minHeight: '100px',
              fontSize: '1rem',
              resize: 'vertical',
              backgroundColor: 'var(--background-card)',
              color: CSS_VAR_TEXT_PRIMARY
            }}
            placeholder="Tell us a bit about yourself..."
            disabled={saving}
            maxLength={500}
          />
          <small style={{ color: CSS_VAR_TEXT_PRIMARY, fontSize: '0.875rem' }}>
            {bio.length}/500 characters
          </small>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button
            onClick={handleSkip}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              fontSize: '1rem',
              flex: '1',
              minWidth: '120px'
            }}
          >
            Skip for Now
          </button>

          <button
            onClick={handleComplete}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              fontSize: '1rem',
              flex: '2',
              minWidth: '120px'
            }}
          >
            {saving ? (uploading ? 'Uploading Photo...' : 'Saving...') : 'Complete Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
